import { type GalleryPreviewData } from '@shared/gallery'
import {
  type AiStatus,
  type AuthProviders,
  type BoardSummary,
  type McpInfo,
  type Me,
} from '@shared/api'

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  /** A human-readable detail from the server, when it gave one. */
  readonly detail: string | undefined
  constructor(status: number, code: string, detail?: string) {
    super(code)
    this.status = status
    this.code = code
    this.detail = detail
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers)
  headers.set('content-type', 'application/json')
  const res = await fetch(`/api${path}`, { ...init, headers })
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string }
    throw new ApiError(res.status, body.error ?? 'error')
  }
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

export const api = {
  gallery: {
    preview: (slug: string) => request<GalleryPreviewData>(`/gallery/${encodeURIComponent(slug)}`),
    copy: (slug: string) =>
      request<BoardSummary>(`/gallery/${encodeURIComponent(slug)}/copy`, { method: 'POST' }),
  },
  config: () => request<{ providers: AuthProviders }>('/config'),
  /** Dev only: the magic link just issued for `email`. */
  devLink: (email: string) =>
    request<{ url: string }>(`/dev-link?email=${encodeURIComponent(email)}`),
  me: () => request<Me>('/me'),
  ai: {
    status: () => request<AiStatus>('/ai/status'),
    saveKey: (apiKey: string) =>
      request<{ last4: string }>('/ai/key', { method: 'PUT', body: JSON.stringify({ apiKey }) }),
    deleteKey: () => request<undefined>('/ai/key', { method: 'DELETE' }),
  },
  mcp: {
    info: () => request<McpInfo>('/mcp'),
    revoke: (id: string) =>
      request<undefined>(`/mcp/apps/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  },
  boards: {
    list: () => request<BoardSummary[]>('/boards'),
    get: (id: string) => request<BoardSummary>(`/boards/${id}`),
    create: (title?: string) =>
      request<BoardSummary>('/boards', { method: 'POST', body: JSON.stringify({ title }) }),
    rename: (id: string, title: string) =>
      request<undefined>(`/boards/${id}`, { method: 'PATCH', body: JSON.stringify({ title }) }),
    remove: (id: string) => request<undefined>(`/boards/${id}`, { method: 'DELETE' }),
    restore: (id: string) => request<undefined>(`/boards/${id}/restore`, { method: 'POST' }),
    /** Where the board's .moot file downloads from (a plain link: the cookie authenticates it). */
    exportUrl: (id: string) => `/api/boards/${id}/export`,
    /** Import a .moot file as a new board. Throws ApiError; `message` says what was wrong with the file. */
    import: async (file: Blob): Promise<BoardSummary> => {
      const res = await fetch('/api/boards/import', {
        method: 'POST',
        headers: { 'content-type': 'application/zip' },
        body: file,
      })
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string; message?: string }
        throw new ApiError(res.status, body.error ?? 'error', body.message)
      }
      return (await res.json()) as BoardSummary
    },
    /** The example board, made once for an account that has never had a board; otherwise null. */
    example: (locale: string) =>
      request<{ board: BoardSummary | null }>('/boards/example', {
        method: 'POST',
        body: JSON.stringify({ locale }),
      }),
  },
}
